import React from 'react';
import { Link } from 'react-router-dom';
import { Article } from '../../types';
import { formatBlogDate, formatReadingTime } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';
import { useBlogSettings } from './blogSettings';

interface FeaturedSectionProps {
  articles: Article[];
}

/** کارت افقی همراه در دسکتاپ (تصویر راست ۴۰٪، متن چپ ۶۰٪) */
const CompanionCard: React.FC<{ article: Article }> = ({ article }) => {
  const href = `/articles/${article.slug}`;
  return (
    <article className="flex overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-white shadow-[0px_1px_3px_0px_rgba(30,32,34,0.04)] transition-shadow hover:shadow-[0px_6px_18px_0px_rgba(30,32,34,0.08)]">
      <Link to={href} className="relative block w-[40%] shrink-0 overflow-hidden rounded-r-[11px] bg-[#efeeeb]" aria-label={article.title}>
        {article.featuredImage && (
          <img
            src={article.featuredImage}
            alt={article.featuredImageAlt || article.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </Link>
      <div className="flex min-w-0 flex-1 flex-col justify-between p-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {article.category?.name && (
              <span className="rounded-[4px] bg-[rgba(239,219,255,0.4)] px-2 py-[2px] text-[12px] font-medium leading-4 text-[#331751]">
                {article.category.name}
              </span>
            )}
            {article.hasVideo && (
              <span className="inline-flex items-center gap-1 text-[12px] font-medium leading-4 text-[#52575e]">
                <BlogIcon name="video-muted" width={14} height={14} />
                همراه ویدیو
              </span>
            )}
          </div>
          <h4 className="blog-clamp-2 pt-[2px] text-[16px] font-bold leading-[22px] text-[#1a1c1a]">
            <Link to={href} className="hover:text-[#331751]">
              {article.title}
            </Link>
          </h4>
          {article.excerpt && <p className="blog-clamp-2 h-10 text-[14px] leading-5 text-[#52575e]">{article.excerpt}</p>}
        </div>
        <div className="pt-2 text-[12px] font-medium leading-4 text-[#52575e]">
          {formatBlogDate(article.publishedAt || article.createdAt)} • {formatReadingTime(article.readingTime)}
        </div>
      </div>
    </article>
  );
};

/** ردیف فشردهٔ موبایل (بندانگشتی ۹۶×۸۰ در راست) */
const CompactRow: React.FC<{ article: Article }> = ({ article }) => {
  const href = `/articles/${article.slug}`;
  return (
    <article className="flex items-center gap-3 rounded-[12px] border border-[#e9e8e5] bg-white p-[10px]">
      <Link to={href} className="block h-20 w-24 shrink-0 overflow-hidden rounded-[8px] bg-[#efeeeb]" aria-label={article.title}>
        {article.featuredImage && (
          <img src={article.featuredImage} alt={article.featuredImageAlt || article.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        )}
      </Link>
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-1 py-[2px]">
        <div className="flex items-center gap-[6px] text-[12px] font-medium leading-5">
          {article.category?.name && <span className="text-[#331751]">{article.category.name}</span>}
          {article.hasVideo && (
            <>
              <span className="text-[16px] leading-6 text-[#cdc3d0]">•</span>
              <span className="inline-flex items-center gap-[2px] text-[#4a2e68]">
                <BlogIcon name="video-purple" width={11.667} height={9.333} />
                ویدیو
              </span>
            </>
          )}
        </div>
        <h4 className="blog-clamp-2 text-[16px] font-semibold leading-[22px] text-[#1a1c1a]">
          <Link to={href}>{article.title}</Link>
        </h4>
        <div className="inline-flex items-center gap-1 text-[12px] font-medium leading-5 text-[#5b5f66]">
          <BlogIcon name="clock-muted-sm" width={11.667} height={11.667} />
          {formatReadingTime(article.readingTime)}
        </div>
      </div>
    </article>
  );
};

/** بخش «منتخب من حقیقی»: یک کارت اصلی + دو کارت همراه */
const FeaturedSection: React.FC<FeaturedSectionProps> = ({ articles }) => {
  const settings = useBlogSettings();
  if (!articles.length) return null;
  const [major, ...companions] = articles;
  const majorHref = `/articles/${major.slug}`;
  const majorDate = formatBlogDate(major.publishedAt || major.createdAt);
  const majorReading = formatReadingTime(major.readingTime);

  return (
    <section aria-labelledby="featured-heading" className="flex flex-col gap-[14px] pt-7 lg:gap-4 lg:pb-6 lg:pt-10">
      {/* هدر بخش */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[6px] lg:gap-2">
          <span className="h-4 w-[6px] rounded-full bg-[#4a2e68] lg:h-[10px] lg:w-[10px] lg:bg-[#331751]" aria-hidden />
          <h2 id="featured-heading" className="text-[18px] font-bold leading-[30px] text-[#331751] lg:text-[24px] lg:leading-8 lg:text-[#1a1c1a]">
            {settings.featuredTitle}
          </h2>
          {settings.featuredBadge && (
            <span className="hidden rounded-[4px] bg-[#e9e8e5] px-2 py-[2px] text-[12px] font-medium leading-4 text-[#52575e] lg:inline-block">
              {settings.featuredBadge}
            </span>
          )}
        </div>
        <span className="text-[12px] font-medium leading-5 text-[#5b5f66] lg:hidden">{settings.featuredBadgeMobile}</span>
      </div>

      {/* دسکتاپ: گرید ۱۲ ستونه (۷ + ۵) */}
      <div className="hidden gap-5 lg:grid lg:grid-cols-12">
        <article className="col-span-7 flex flex-col self-start overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-white shadow-[0px_1px_3px_0px_rgba(30,32,34,0.04)]">
          <Link to={majorHref} className="relative block aspect-video w-full overflow-hidden rounded-t-[11px] bg-[#efeeeb]" aria-label={major.title}>
            {major.featuredImage && (
              <img
                src={major.featuredImage}
                alt={major.featuredImageAlt || major.title}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
              />
            )}
            {major.category?.name && (
              <span className="absolute right-3 top-3 rounded-[6px] border border-[#e9e8e5] bg-white/90 px-[10px] py-1 text-[12px] font-semibold leading-4 text-[#331751] backdrop-blur-[4px]">
                {major.category.name}
              </span>
            )}
          </Link>
          <div className="flex flex-1 flex-col justify-between p-5">
            <div className="flex flex-col gap-2 pb-3">
              <h3 className="text-[24px] font-bold leading-8 text-[#1a1c1a]">
                <Link to={majorHref} className="hover:text-[#331751]">
                  {major.title}
                </Link>
              </h3>
              {major.excerpt && <p className="blog-clamp-2 text-[16px] leading-[26px] text-[#52575e]">{major.excerpt}</p>}
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-[14px] font-medium leading-5 text-[#52575e]">
                {majorDate} • {majorReading}
              </span>
              <Link to={majorHref} className="inline-flex items-center gap-[2px] text-[14px] font-semibold leading-5 text-[#331751] hover:text-[#392352]">
                مطالعه
                <BlogIcon name="chevron-left-xs" width={2.917} height={5.833} />
              </Link>
            </div>
          </div>
        </article>

        {companions.length > 0 && (
          <div className="col-span-5 flex flex-col self-start">
            {companions.slice(0, 2).map((item) => (
              <CompanionCard key={item.id} article={item} />
            ))}
          </div>
        )}
      </div>

      {/* موبایل و تبلت: کارت هیرو + ردیف‌های فشرده */}
      <div className="flex flex-col gap-3 lg:hidden">
        <article className="overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
          <Link to={majorHref} className="relative block aspect-[16/9] w-full overflow-hidden rounded-t-[11px] bg-[#efeeeb]" aria-label={major.title}>
            {major.featuredImage && (
              <img src={major.featuredImage} alt={major.featuredImageAlt || major.title} loading="eager" decoding="async" className="h-full w-full object-cover" />
            )}
            {major.category?.name && (
              <span className="absolute right-[10px] top-[10px] rounded-[6px] bg-[rgba(250,249,246,0.9)] px-[10px] py-1 text-[12px] font-medium leading-5 text-[#331751] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] backdrop-blur-[2px]">
                {major.category.name}
              </span>
            )}
          </Link>
          <div className="flex flex-col gap-[10px] p-[14px]">
            <h3 className="text-[18px] font-bold leading-[24.75px] text-[#1a1c1a]">
              <Link to={majorHref}>{major.title}</Link>
            </h3>
            <div className="flex items-center justify-between text-[12px] font-medium leading-5 text-[#5b5f66]">
              <span className="inline-flex items-center gap-1">
                <BlogIcon name="calendar" width={11.25} height={12.5} />
                {majorDate}
              </span>
              <span className="inline-flex items-center gap-1">
                <BlogIcon name="clock" width={12.5} height={12.5} />
                {majorReading}
              </span>
            </div>
          </div>
        </article>
        {companions.length > 0 && (
          <div className="flex flex-col gap-[10px]">
            {companions.slice(0, 2).map((item) => (
              <CompactRow key={item.id} article={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedSection;
